import React, { useState, useEffect, useCallback, useRef, useMemo, startTransition } from 'react';
import { Container, Card, Button, Table, Spinner, Alert, Badge, Form, Image, Dropdown, Pagination } from 'react-bootstrap';
import Select from 'react-select';
import { IconSearch, IconRefreshCw, IconPlus, IconTrash, IconCheck, IconX, IconCheckSquare } from '../components/Icons';
import CheckerModal from '../components/CheckerModal';
import { loadCheckerPanelState, saveCheckerPanelState } from '../utils/checkerPanelStorage';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  fetchProductBySku, 
  fetchNewWaveProducts,
  saveNewWaveProduct, 
  updateProduct, 
  removeProduct,
  clearError,
  setValidationErrors,
  clearValidationErrors,
  setNewWavePagination,
  NewWaveProduct
} from '../store/slices/newWaveSlice';
import { fetchFilterOptions, confirmNewProduct } from '../store/slices/productsSlice';
import { api } from '../services/api';
import { fetchConstants } from '../store/slices/constantsSlice';
import { fetchCertificates } from '../store/slices/certificatesSlice';
import {
  hasNewFormulaConstants,
  calculateNewFormula,
  getFboWb,
  isPlaceholderPurchasePercent,
  type SppRecord,
  type FboWbRecord,
  type FboOzonData,
} from '../utils/newFormulaCalculations';
import { extractBaseSku } from '../utils/skuUtils';
import type { Certificate } from '../store/slices/certificatesSlice';

// Интерфейс для дополнительных данных товара (аналогично NewProducts)
interface ProductData {
  id: string;
  name: string;
  article?: string;
  requiredPrice: number;
  certificate: string;
  orderQuantity: string;
  packageLength: number;
  packageWidth: number;
  packageHeight: number;
  purchasePercent: number;
  calculatedCostPrice?: number;
  calculatedYuanPrice?: number;
  manualYuanPrice?: number | null; // Ручное значение цены в юанях
}

// Тип для полей drag
type DragFieldType = 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity' | 'subject_id' | 'purchasePercent';

interface NewWaveRowProps {
  product: NewWaveProduct;
  data: ProductData;
  errors: Record<string, boolean | undefined>;
  hasErrors: boolean;
  calculatedCosts: { costPrice: number; yuanPrice?: number } | null;
  subjectOptions: { value: number; label: string }[];
  certificates: Certificate[];
  allSubjects: Array<{ id: number; name: string }>;
  tempInputValuesRow?: { requiredPrice?: string; orderQuantity?: string; packageLength?: string; packageWidth?: string; packageHeight?: string; purchasePercent?: string };
  tempYuanPriceRow?: string;
  recalculatedYuan: boolean;
  isUpdating: boolean;
  isSaving: boolean;
  isSelected: boolean;
  checkerModalShow: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  draggedProductId: string | null;
  draggedFieldType: DragFieldType | null;
  dragOverFieldTypes: Set<'packageLength' | 'packageWidth' | 'packageHeight'>;
  fileInputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  onRowClick: (productId: string) => void;
  setCheckerModal: React.Dispatch<React.SetStateAction<{ show: boolean; initialSku?: string; initialSearchAsSubject?: boolean }>>;
  saveCheckerPanelState: (state: { open?: boolean }) => void;
  onNameChange: (productId: string, name: string) => void;
  onSubjectChange: (productId: string, subjectId: number | null) => void | Promise<void>;
  onConfirmRecalculatedPrice: (productId: string) => void | Promise<void>;
  onRevertRecalculatedPrice: (productId: string) => void;
  onUrlChange: (productId: string, url: string) => void;
  onImageUpload: (productId: string, file: File) => void;
  onImageDelete: (productId: string) => void;
  onSaveProduct: (product: NewWaveProduct) => void | Promise<void>;
  onRemoveProduct: (productId: string) => void | Promise<void>;
  onDragStart: (e: React.DragEvent, productId: string, fieldType: DragFieldType, value: number | string) => void;
  onDragOver: (e: React.DragEvent, productId: string, targetFieldType?: DragFieldType) => void;
  onDrop: (e: React.DragEvent, productId: string, targetFieldType?: DragFieldType) => void | Promise<void>;
  onDragEnd: () => void;
  onUpdateProductData: (productId: string, updates: Partial<ProductData>, saveImmediately?: boolean) => void;
  roundOrderQuantity: (value: string | number, price?: number) => string;
  formatCurrency: (amount: number) => string;
  getFullImageUrl: (url: string | null) => string;
  formatSubjectName: (name: string) => string;
  onTempYuanChange?: (productId: string, value: string) => void;
  onBlurYuan?: (productId: string, value: string) => void;
  articleError?: string;
  onClearArticleError?: () => void;
}

const NewWaveRow = React.memo(function NewWaveRow(props: NewWaveRowProps) {
  const {
    product,
    data,
    errors,
    hasErrors,
    calculatedCosts,
    subjectOptions,
    certificates,
    tempInputValuesRow,
    tempYuanPriceRow,
    recalculatedYuan,
    isUpdating,
    isSaving,
    isSelected,
    checkerModalShow,
    isDragging,
    isDragOver,
    draggedProductId,
    draggedFieldType,
    dragOverFieldTypes,
    fileInputRefs,
    onRowClick,
    setCheckerModal,
    saveCheckerPanelState,
    onNameChange,
    onSubjectChange,
    onConfirmRecalculatedPrice,
    onRevertRecalculatedPrice,
    onUrlChange,
    onImageUpload,
    onImageDelete,
    onSaveProduct,
    onRemoveProduct,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onUpdateProductData,
    roundOrderQuantity,
    formatCurrency,
    getFullImageUrl,
    formatSubjectName,
    onTempYuanChange,
    onBlurYuan,
    articleError,
    onClearArticleError,
  } = props;

  return (
    <tr
      className={hasErrors ? 'table-danger' : (product.isSaved && product.new_product && product.target_price_cny) ? 'table-success' : ''}
      style={{
        backgroundColor: isSelected ? 'rgba(0, 123, 255, 0.2)' : 'transparent',
        cursor: checkerModalShow ? 'pointer' : 'default'
      }}
      onClick={(e) => {
        e.stopPropagation();
        onRowClick(product.id);
        if (checkerModalShow) {
          setCheckerModal(prev => ({ ...prev, initialSku: String(product.id), initialSearchAsSubject: false }));
        }
      }}
      onDragOver={(e) => {
        if (isDragging) {
          onDragOver(e, product.id);
        }
      }}
      onDrop={(e) => {
        if (isDragging) {
          onDrop(e, product.id);
        }
      }}
    >
      <td>
        {product.thumb_url ? (
          <Image src={getFullImageUrl(product.thumb_url)} alt={product.name || ''} width={50} height={50} className="rounded" style={{ objectFit: 'cover' }} />
        ) : (
          <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
            <IconPlus className="text-muted" />
          </div>
        )}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {product.id ? (
          <a href={`https://www.wildberries.ru/catalog/${extractBaseSku(product.id)}/detail.aspx`} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">
            {product.id}
          </a>
        ) : (
          <span className="text-muted">N/A</span>
        )}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <Form.Control
          key={product.article || 'empty'}
          type="text"
          size="sm"
          defaultValue={product.article || ''}
          disabled={!product.isSaved}
          placeholder={product.isSaved ? 'Артикул' : 'после сохранения'}
          isInvalid={!!articleError}
          title={articleError || undefined}
          autoComplete="off"
          spellCheck={false}
          style={{ minWidth: '140px', textAlign: 'center' }}
          onFocus={(e) => {
            onClearArticleError?.();
            e.target.select();
          }}
          onBlur={(e) => {
            const persisted = String(product.article || '').trim();
            const next = e.target.value.trim();
            if (!next) {
              e.target.value = persisted;
              return;
            }
            if (next === persisted) return;
            onUpdateProductData(product.id, { article: next }, true);
          }}
        />
      </td>
      <td style={{ minWidth: '300px' }} onClick={(e) => e.stopPropagation()}>
        {errors.name && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <Form.Control as="textarea" rows={2} size="sm" value={data.name} onChange={(e) => onNameChange(product.id, e.target.value)} placeholder="Название товара" isInvalid={errors.name} style={{ resize: 'vertical', minHeight: '38px' }} />
      </td>
      <td onClick={(e) => e.stopPropagation()} draggable={product.subject_id !== null && product.subject_id !== undefined} onDragStart={(e) => { if (product.subject_id != null) { onDragStart(e, product.id, 'subject_id', product.subject_id); } else { e.preventDefault(); } }} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, product.id, 'subject_id'); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, product.id, 'subject_id'); }} onDragEnd={onDragEnd} style={{ cursor: isDragging && draggedProductId === product.id && draggedFieldType === 'subject_id' ? 'grabbing' : (product.subject_id ? 'grab' : 'default'), minWidth: '250px' }} className={isDragOver && draggedFieldType === 'subject_id' ? 'drag-over-cell' : ''}>
        <Select
          options={subjectOptions}
          value={subjectOptions.find(opt => opt.value === product.subject_id) || null}
          onChange={(option) => onSubjectChange(product.id, option?.value || null)}
          placeholder="Выберите subject"
          isSearchable
          isClearable
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{
            control: (base) => ({ ...base, backgroundColor: '#212529', borderColor: product.validationErrors?.subject_id ? '#dc3545' : '#495057', color: '#fff', minWidth: '250px' }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, backgroundColor: '#212529', color: '#fff', minWidth: '400px', width: 'max-content', zIndex: 9999 }),
            menuList: (base) => ({ ...base, maxHeight: '300px' }),
            option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#495057' : '#212529', color: '#fff', whiteSpace: 'nowrap', overflow: 'visible' }),
            singleValue: (base) => ({ ...base, color: '#fff' }),
            input: (base) => ({ ...base, color: '#fff' })
          }}
        />
      </td>
      <td style={{ minWidth: '100px' }} onClick={(e) => e.stopPropagation()}>
        {product.validationErrors?.url && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <Form.Control type="text" size="sm" placeholder="https://www.wildberries.ru/catalog/..." value={product.url || ''} onChange={(e) => onUrlChange(product.id, e.target.value)} isInvalid={product.validationErrors?.url} style={{ fontSize: '0.85rem' }} />
      </td>
      <td style={{ minWidth: '100px' }} onClick={(e) => e.stopPropagation()}>
        {product.validationErrors?.thumb_url && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <div className="d-flex align-items-center gap-2">
          {product.thumb_url ? (
            <>
              <Image src={getFullImageUrl(product.thumb_url)} alt="Product" width={50} height={50} className="rounded" style={{ objectFit: 'cover' }} />
              <Button variant="outline-danger" size="sm" onClick={(e) => { e.stopPropagation(); onImageDelete(product.id); }}><IconTrash size={14} /></Button>
            </>
          ) : (
            <div className="d-flex flex-column align-items-center">
              <input ref={(el) => { fileInputRefs.current[product.id] = el; }} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const file = e.target.files?.[0]; if (file) onImageUpload(product.id, file); }} />
              <Button variant="outline-secondary" size="sm" onClick={(e) => { e.stopPropagation(); fileInputRefs.current[product.id]?.click(); }}><IconPlus size={14} className="me-1" />Загрузить</Button>
            </div>
          )}
        </div>
      </td>
      <td draggable onDragStart={(e) => onDragStart(e, product.id, 'requiredPrice', data.requiredPrice || 0)} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, product.id, 'requiredPrice'); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, product.id, 'requiredPrice'); }} onDragEnd={onDragEnd} style={{ cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab', minWidth: '100px' }} className={isDragOver ? 'drag-over-cell' : ''}>
        {errors.requiredPrice && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <input type="number" value={tempInputValuesRow?.requiredPrice !== undefined ? tempInputValuesRow.requiredPrice : (data.requiredPrice || 0).toString()} onChange={(e) => { const value = e.target.value; onUpdateProductData(product.id, { requiredPrice: parseFloat(value) || 0 }); }} onFocus={(e) => { e.target.select(); }} min={0} step={0.01} className={`form-control form-control-sm ${errors.requiredPrice ? 'is-invalid' : ''}`} onClick={(e) => e.stopPropagation()} onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }} />
      </td>
      <td draggable onDragStart={(e) => onDragStart(e, product.id, 'certificate', data.certificate || '')} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, product.id, 'certificate'); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, product.id, 'certificate'); }} onDragEnd={onDragEnd} style={{ cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab', minWidth: '100px' }} className={isDragOver ? 'drag-over-cell' : ''}>
        <select key={`certificate-${product.id}-${data.certificate}`} value={data.certificate || ''} onChange={(e) => onUpdateProductData(product.id, { certificate: e.target.value })} className="form-select form-select-sm" onClick={(e) => e.stopPropagation()} onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}>
          <option value="">Выберите</option>
          {certificates.map((certificate: Certificate) => <option key={certificate.id} value={certificate.id.toString()}>{certificate.name}</option>)}
        </select>
      </td>
      <td draggable onDragStart={(e) => onDragStart(e, product.id, 'orderQuantity', data.orderQuantity || '')} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, product.id, 'orderQuantity'); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, product.id, 'orderQuantity'); }} onDragEnd={onDragEnd} style={{ cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab', minWidth: '100px' }} className={isDragOver ? 'drag-over-cell' : ''}>
        {errors.orderQuantity && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <input
          type="text"
          value={tempInputValuesRow?.orderQuantity !== undefined ? tempInputValuesRow.orderQuantity : (data.orderQuantity || '')}
          onChange={(e) => {
            const rawValue = e.target.value;
            onUpdateProductData(product.id, { orderQuantity: rawValue.trim() });
          }}
          onBlur={(e) => {
            const raw = e.target.value.trim();
            if (raw) {
              const rounded = roundOrderQuantity(raw, data.requiredPrice);
              onUpdateProductData(product.id, { orderQuantity: rounded });
            }
          }}
          onFocus={(e) => e.target.select()}
          placeholder="Введите количество"
          className={`form-control form-control-sm ${errors.orderQuantity ? 'is-invalid' : ''}`}
          onClick={(e) => e.stopPropagation()}
          onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
        />
        {product.sales && <div className="text-muted small mt-1" style={{ fontSize: '11px' }}>продажи: {product.sales}</div>}
      </td>
      <td draggable onDragStart={(e) => onDragStart(e, product.id, 'packageLength', data.packageLength || 0)} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, product.id, 'packageLength'); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, product.id, 'packageLength'); }} onDragEnd={onDragEnd} style={{ cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab', minWidth: '100px' }} className={isDragOver ? 'drag-over-cell' : ''}>
        {errors.packageLength && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <input type="number" value={tempInputValuesRow?.packageLength !== undefined ? tempInputValuesRow.packageLength : (data.packageLength || 0).toString()} onChange={(e) => onUpdateProductData(product.id, { packageLength: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} min={0} step={0.1} className={`form-control form-control-sm ${errors.packageLength ? 'is-invalid' : ''}`} onClick={(e) => e.stopPropagation()} onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }} />
      </td>
      <td draggable onDragStart={(e) => onDragStart(e, product.id, 'packageWidth', data.packageWidth || 0)} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, product.id, 'packageWidth'); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, product.id, 'packageWidth'); }} onDragEnd={onDragEnd} style={{ cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab', minWidth: '100px' }} className={isDragOver ? 'drag-over-cell' : ''}>
        {errors.packageWidth && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <input type="number" value={tempInputValuesRow?.packageWidth !== undefined ? tempInputValuesRow.packageWidth : (data.packageWidth || 0).toString()} onChange={(e) => onUpdateProductData(product.id, { packageWidth: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} min={0} step={0.1} className={`form-control form-control-sm ${errors.packageWidth ? 'is-invalid' : ''}`} onClick={(e) => e.stopPropagation()} onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }} />
      </td>
      <td draggable onDragStart={(e) => onDragStart(e, product.id, 'packageHeight', data.packageHeight || 0)} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, product.id, 'packageHeight'); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, product.id, 'packageHeight'); }} onDragEnd={onDragEnd} style={{ cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab', minWidth: '100px' }} className={isDragOver ? 'drag-over-cell' : ''}>
        {errors.packageHeight && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <input type="number" value={tempInputValuesRow?.packageHeight !== undefined ? tempInputValuesRow.packageHeight : (data.packageHeight || 0).toString()} onChange={(e) => onUpdateProductData(product.id, { packageHeight: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} min={0} step={0.1} className={`form-control form-control-sm ${errors.packageHeight ? 'is-invalid' : ''}`} onClick={(e) => e.stopPropagation()} onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }} />
      </td>
      <td draggable onDragStart={(e) => onDragStart(e, product.id, 'purchasePercent', data.purchasePercent || 0)} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, product.id, 'purchasePercent'); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, product.id, 'purchasePercent'); }} onDragEnd={onDragEnd} style={{ cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab' }} className={isDragOver ? 'drag-over-cell' : ''}>
        {errors.purchasePercent && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Нельзя 100%</div>}
        <div className="d-flex align-items-center">
          <input type="number" value={tempInputValuesRow?.purchasePercent !== undefined ? tempInputValuesRow.purchasePercent : (data.purchasePercent || 0).toString()} onChange={(e) => onUpdateProductData(product.id, { purchasePercent: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} min={0} max={100} step={0.01} className={`form-control form-control-sm ${errors.purchasePercent ? 'is-invalid' : ''}`} style={{ minWidth: '100px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()} onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }} />
          <span className="ms-1">%</span>
        </div>
      </td>
      <td style={{ minWidth: '100px' }}>{calculatedCosts ? formatCurrency(calculatedCosts.costPrice) : '-'}</td>
      <td>
        {errors.manualYuanPrice && <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>Обязательное поле</div>}
        <div className="d-flex align-items-center gap-1">
          <div className="d-flex align-items-center" style={{ backgroundColor: recalculatedYuan ? '#fff3cd' : 'transparent', borderRadius: '4px', padding: '2px', flex: 1 }}>
            <input type="number" value={tempYuanPriceRow !== undefined ? tempYuanPriceRow : (data.manualYuanPrice != null ? data.manualYuanPrice.toString() : (data.calculatedYuanPrice != null && !isNaN(data.calculatedYuanPrice) ? data.calculatedYuanPrice.toString() : ''))} onChange={(e) => { const value = e.target.value; onTempYuanChange?.(product.id, value); const num = parseFloat(value.trim()); if (!isNaN(num)) onUpdateProductData(product.id, { manualYuanPrice: num }); }} onBlur={(e) => onBlurYuan?.(product.id, e.target.value.trim())} onFocus={(e) => e.target.select()} min={0} step={0.01} className={`form-control form-control-sm ${errors.manualYuanPrice ? 'is-invalid' : ''}`} style={{ minWidth: '100px', textAlign: 'center', backgroundColor: 'transparent', border: 'none' }} onClick={(e) => e.stopPropagation()} placeholder={calculatedCosts?.yuanPrice != null && !isNaN(calculatedCosts.yuanPrice) ? calculatedCosts.yuanPrice.toFixed(2) : '-'} />
            <span className="ms-1">¥</span>
          </div>
          {product.isSaved && recalculatedYuan && (
            <>
              <Button variant="success" size="sm" className="p-1" style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => { e.stopPropagation(); onConfirmRecalculatedPrice(product.id); }} disabled={isUpdating} title="Подтвердить новое значение">{isUpdating ? <Spinner size="sm" animation="border" /> : <IconCheck size={14} />}</Button>
              <Button variant="danger" size="sm" className="p-1" style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => { e.stopPropagation(); onRevertRecalculatedPrice(product.id); }} title="Вернуть старое значение"><IconX size={14} /></Button>
            </>
          )}
        </div>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-center gap-2">
          <Button variant={product.isSaved ? 'secondary' : 'primary'} size="sm" onClick={(e) => { e.stopPropagation(); onSaveProduct(product); }} disabled={product.isSaving || product.isSaved} title={product.isSaving ? 'Сохранение...' : product.isSaved ? 'Сохранен' : 'Сохранить'}>{product.isSaving ? <Spinner size="sm" animation="border" /> : <IconCheck size={16} />}</Button>
          <Button variant="outline-danger" size="sm" onClick={(e) => { e.stopPropagation(); onRemoveProduct(product.id); }}><IconTrash size={14} /></Button>
          <Dropdown>
            <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted" title="Открыть в чекере"><IconCheckSquare size={12} /></Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => { saveCheckerPanelState({ open: true }); setCheckerModal(prev => ({ ...prev, show: true, initialSku: String(product.id), initialSearchAsSubject: false })); }}>Открыть тут</Dropdown.Item>
              <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank')}>Открыть в новой вкладке</Dropdown.Item>
              <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank', 'noopener,noreferrer,width=900,height=700')}>Открыть в новом окне</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </td>
    </tr>
  );
});

const NewWave: React.FC = () => {
  const dispatch = useAppDispatch();
  const { products, loading, error, fetchingSku, fetchingSkuError, pagination } = useAppSelector((state) => state.newWave);
  const { filterOptions } = useAppSelector((state) => state.products);
  const { constants } = useAppSelector((state) => state.constants);
  const { certificates } = useAppSelector((state) => state.certificates);
  
  const [allSubjects, setAllSubjects] = useState<Array<{ id: number; name: string }>>([]);
  
  const [skuInput, setSkuInput] = useState('');
  const [savingProducts, setSavingProducts] = useState<Set<string>>(new Set());
  const [updatingProducts, setUpdatingProducts] = useState<Set<string>>(new Set());
  const [articleFieldErrors, setArticleFieldErrors] = useState<Record<string, string>>({});
  const [productData, setProductData] = useState<Record<string, ProductData>>({});
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  // Временное состояние для поля "Итог цены в юанях" во время редактирования
  const [tempYuanPrice, setTempYuanPrice] = useState<Record<string, string>>({});
  // Временное состояние для полей ввода, чтобы избежать сброса при ререндере
  const [tempInputValues, setTempInputValues] = useState<Record<string, {
    requiredPrice?: string;
    orderQuantity?: string;
    packageLength?: string;
    packageWidth?: string;
    packageHeight?: string;
    purchasePercent?: string;
  }>>({});
  const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, {
    name?: boolean;
    orderQuantity?: boolean;
    packageLength?: boolean;
    packageWidth?: boolean;
    packageHeight?: boolean;
    requiredPrice?: boolean;
  }>>({});
  
  // Состояния для отслеживания пересчета цены в юанях
  const [originalYuanPrices, setOriginalYuanPrices] = useState<Record<string, number | null>>({});
  const [recalculatedYuanPrices, setRecalculatedYuanPrices] = useState<Record<string, boolean>>({});
  const originalYuanPricesRef = useRef<Record<string, number | null>>({});
  
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const imageFilesRef = useRef<Record<string, File>>({});
  const saveTimersRef = useRef<Record<string, NodeJS.Timeout>>({});
  const dragOverStateRef = useRef<{ productIds: Set<string>; fieldTypes: Set<'packageLength' | 'packageWidth' | 'packageHeight'> } | null>(null);
  const dragOverRafRef = useRef<number | null>(null);
  const productDataRef = useRef<Record<string, ProductData>>({});

  useEffect(() => {
    productDataRef.current = productData;
  }, [productData]);

  // Синхронизируем ref с state
  useEffect(() => {
    originalYuanPricesRef.current = originalYuanPrices;
  }, [originalYuanPrices]);

  // Состояния для drag and drop
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const [draggedFieldType, setDraggedFieldType] = useState<'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity' | 'subject_id' | 'purchasePercent' | null>(null);
  const [draggedValue, setDraggedValue] = useState<number | string | null>(null);
  const [dragOverProductIds, setDragOverProductIds] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [changingFieldsBatch, setChangingFieldsBatch] = useState<Set<string>>(new Set());
  const [dragOverFieldTypes, setDragOverFieldTypes] = useState<Set<'packageLength' | 'packageWidth' | 'packageHeight'>>(new Set());

  const [checkerModal, setCheckerModal] = useState<{ show: boolean; initialSku?: string; initialSearchAsSubject?: boolean }>(() => {
    const saved = loadCheckerPanelState();
    return { show: saved.open };
  });

  // Данные для нового калькулятора (SPP / FBO)
  const [sppData, setSppData] = useState<SppRecord[]>([]);
  const [fboWbData, setFboWbData] = useState<FboWbRecord[]>([]);
  const [fboOzonData, setFboOzonData] = useState<FboOzonData>({});

  // Загрузка опций фильтров, констант и сертификатов при монтировании
  useEffect(() => {
    dispatch(fetchFilterOptions());
    dispatch(fetchConstants());
    dispatch(fetchCertificates());
    const loadAllSubjects = async () => {
      try {
        const response = await api.getAllSubjects();
        if (response.data?.success && response.data?.data) {
          setAllSubjects(response.data.data);
        } else if (Array.isArray(response.data)) {
          setAllSubjects(response.data);
        }
      } catch (error) {
        console.error('Failed to load all subjects:', error);
      }
    };
    loadAllSubjects();
  }, [dispatch]);

  // Загрузка товаров New Wave с пагинацией (по 50 на страницу)
  useEffect(() => {
    dispatch(fetchNewWaveProducts({ page: pagination.page, limit: pagination.limit }));
  }, [dispatch, pagination.page, pagination.limit]);

  // Cleanup таймеров при размонтировании
  useEffect(() => {
    return () => {
      Object.values(saveTimersRef.current).forEach(timer => clearTimeout(timer));
      saveTimersRef.current = {};
    };
  }, []);

  // Функция для принудительного округления количества заказа (логика как в NewProducts/Products)
  // При цене >= order_rounding: округление не применяется — значение возвращается как есть. Иначе: минимум 200, от 200 до 1000 - шаг 50, после 1000 - шаг 100
  const roundOrderQuantity = useCallback((value: string | number, price?: number): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    const orderRoundingRaw = constants.find(c => (c.name ?? '').trim() === 'order_rounding')?.value;
    const orderRounding = (orderRoundingRaw != null && !Number.isNaN(Number(orderRoundingRaw)))
      ? Number(orderRoundingRaw)
      : 8000;

    if (price !== undefined && price >= orderRounding) {
      return typeof value === 'string' ? value.trim() : String(value);
    }
    if (numValue < 200) return '200';
    if (numValue < 1000) return Math.ceil(numValue / 50) * 50 + '';
    return Math.ceil(numValue / 100) * 100 + '';
  }, [constants]);

  // Загрузка данных для нового калькулятора (SPP / FBO), когда есть константы new_formula
  useEffect(() => {
    if (!hasNewFormulaConstants(constants)) return;
    let cancelled = false;
    Promise.all([
      api.getSppMarketplace(),
      api.getFboWb(),
      api.getFboOzon(),
    ]).then(([sppRes, fboWbRes, fboOzRes]) => {
      if (cancelled) return;
      if (sppRes?.success && sppRes?.data) setSppData(sppRes.data);
      if (fboWbRes?.success && fboWbRes?.data) setFboWbData(fboWbRes.data);
      if (fboOzRes?.success && fboOzRes?.data) setFboOzonData(fboOzRes.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [constants]);

  // Функция для автоматического расчета себестоимости (только новая формула)
  type CalculatedCostsResult = {
    costPrice: number;
    yuanPrice: number;
    target_price_cny_wb?: number;
    target_price_rub_wb?: number;
    target_price_cny_ozon?: number;
    target_price_rub_ozon?: number;
  } | null;
  const calculateProductCosts = useCallback((data: ProductData, product?: NewWaveProduct | null): CalculatedCostsResult => {
    if (!data.packageLength || !data.packageWidth || !data.packageHeight || !data.requiredPrice || data.requiredPrice <= 0) {
      return null;
    }

    const quantity = data.orderQuantity ? parseInt(data.orderQuantity) || 100 : 100;
    const useNewFormula = hasNewFormulaConstants(constants) && sppData.length > 0 && fboWbData.length > 0;

    if (!useNewFormula) return null;

    const subjectName = product ? (allSubjects.find(s => s.id === product.subject_id)?.name ?? '') : '';
    const commissionFbo = getFboWb(subjectName, fboWbData) ?? null;

    const certId = data.certificate ? parseInt(data.certificate) : null;
    const selectedCertificate = certId ? certificates.find((cert: Certificate) => cert.id === certId) ?? null : null;

    try {
      const result = calculateNewFormula({
        requiredPrice: data.requiredPrice,
        packageLength: data.packageLength,
        packageWidth: data.packageWidth,
        packageHeight: data.packageHeight,
        subjectName,
        quantity,
        commissionFbo,
        certificate: selectedCertificate,
        constants,
        sppData,
        fboWbData,
        fboOzonData,
        purchasePercent: data.purchasePercent ?? product?.purchasePercent ?? null,
      });
      return {
        costPrice: result.target_price_rub,
        yuanPrice: result.target_price_cny,
        target_price_cny_wb: result.target_price_cny_wb,
        target_price_rub_wb: result.target_price_rub_wb,
        target_price_cny_ozon: result.target_price_cny_ozon,
        target_price_rub_ozon: result.target_price_rub_ozon,
      };
    } catch (error) {
      console.error('Error calculating costs:', error);
      return null;
    }
  }, [constants, certificates, sppData, fboWbData, fboOzonData, allSubjects]);

  // Инициализация данных для товаров (без setState внутри setState — один батч для originalYuanPrices)
  useEffect(() => {
    const collectedOriginalPrices: Record<string, number | null> = {};

    setProductData(prev => {
      const newData = { ...prev };
      let hasChanges = false;

      products.forEach(product => {
        const existingData = newData[product.id];
        const newManualYuanPrice = product.target_price_cny !== null && product.target_price_cny !== undefined ? product.target_price_cny : null;

        if (product.isSaved && product.target_price_cny !== null && product.target_price_cny !== undefined) {
          if (originalYuanPricesRef.current[product.id] === undefined) {
            collectedOriginalPrices[product.id] = product.target_price_cny ?? null;
          }
        }

        if (!existingData) {
          hasChanges = true;
          newData[product.id] = {
            id: product.id,
            name: product.name || '',
            requiredPrice: product.requiredPrice || product.final_price_average || product.final_price || 0,
            certificate: product.certificate || '',
            orderQuantity: product.orderQuantity || '',
            packageLength: product.packageLength || 0,
            packageWidth: product.packageWidth || 0,
            packageHeight: product.packageHeight || 0,
            purchasePercent: product.purchasePercent || 0,
            calculatedCostPrice: undefined,
            calculatedYuanPrice: undefined,
            manualYuanPrice: newManualYuanPrice
          };
        } else if (existingData.manualYuanPrice !== newManualYuanPrice) {
          hasChanges = true;
          newData[product.id] = {
            ...existingData,
            manualYuanPrice: newManualYuanPrice
          };
        }
      });

      return hasChanges ? newData : prev;
    });

    if (Object.keys(collectedOriginalPrices).length > 0) {
      setOriginalYuanPrices(prev => ({ ...prev, ...collectedOriginalPrices }));
      Object.assign(originalYuanPricesRef.current, collectedOriginalPrices);
    }
  }, [products]);

  // Автоматический пересчет себестоимости при изменении констант/сертификатов/формулы или списка товаров (products меняется только при add/remove/load, не при вводе)
  useEffect(() => {
    if (constants.length > 0 && certificates.length > 0) {
      setProductData(prev => {
        const newData = { ...prev };
        let hasChanges = false;

        Object.keys(newData).forEach(productId => {
          const data = newData[productId];
          const product = products.find(p => p.id === productId);
          const calculatedCosts = calculateProductCosts(data, product);
          const shouldRecalculateYuanPrice = data.manualYuanPrice === null;

          if (calculatedCosts) {
            const newCalculatedYuanPrice = shouldRecalculateYuanPrice ? calculatedCosts.yuanPrice : data.calculatedYuanPrice;

            if (data.calculatedCostPrice !== calculatedCosts.costPrice ||
                (shouldRecalculateYuanPrice && data.calculatedYuanPrice !== calculatedCosts.yuanPrice)) {
              newData[productId] = {
                ...data,
                calculatedCostPrice: calculatedCosts.costPrice,
                calculatedYuanPrice: newCalculatedYuanPrice
              };
              hasChanges = true;
            }
          }
        });

        return hasChanges ? newData : prev;
      });
    }
  }, [constants, certificates, calculateProductCosts, products]);

  // Обработчик поиска товара по SKU
  const handleSearchSku = useCallback(async () => {
    if (!skuInput.trim()) return;
    const sku = skuInput.trim();
    setSkuInput('');
    try {
      await dispatch(fetchProductBySku(sku)).unwrap();
    } catch (error: any) {
      console.error('Failed to fetch product by SKU:', error);
    }
  }, [dispatch, skuInput]);

  // Функция сохранения товара в БД
  const saveProductToDB = useCallback(async (productId: string, updates: Partial<ProductData>) => {
    const product = products.find(p => p.id === productId);
    if (!product || !product.isSaved) return;

    setUpdatingProducts(prev => new Set(prev).add(productId));

    try {
      const updatePayload: any = {};
      const nextArticle = updates.article !== undefined ? String(updates.article).trim() : undefined;
      const persistedArticle = String(product.article || '').trim();
      const isArticleOnly = updates.article !== undefined && Object.keys(updates).every((k) => k === 'article');

      if (nextArticle !== undefined) {
        if (!nextArticle || nextArticle === persistedArticle) return;
        updatePayload.article = nextArticle;
      }

      if (!isArticleOnly) {
        if (updates.requiredPrice !== undefined) {
          updatePayload.required_price = updates.requiredPrice;
        }
        if (updates.certificate !== undefined) {
          updatePayload.cert_id = updates.certificate === '' ? null : parseInt(updates.certificate) || null;
        }
        if (updates.orderQuantity !== undefined) {
          updatePayload.order_quantity = updates.orderQuantity === '' ? null : parseInt(updates.orderQuantity) || null;
        }
        if (updates.packageLength !== undefined) {
          updatePayload.package_length = updates.packageLength;
        }
        if (updates.packageWidth !== undefined) {
          updatePayload.package_width = updates.packageWidth;
        }
        if (updates.packageHeight !== undefined) {
          updatePayload.package_height = updates.packageHeight;
        }
        if (updates.purchasePercent !== undefined) {
          updatePayload.purchase_percent = updates.purchasePercent;
        }
        if (updates.name !== undefined) {
          updatePayload.name = updates.name;
        }
        if (updates.manualYuanPrice !== undefined) {
          updatePayload.target_price_cny = updates.manualYuanPrice === null ? null : parseFloat(updates.manualYuanPrice.toString()) || null;
        }

        // Добавляем рассчитанные таргеты WB и Ozon при сохранении; FBO берём из таблицы по subject
        const currentData = { ...productDataRef.current[productId], ...updates };
        const calculatedCosts = calculateProductCosts(currentData, product);
        if (product.subject_id) {
          const subjectName = allSubjects.find(s => s.id === product.subject_id)?.name ?? '';
          const fboFromTable = getFboWb(subjectName, fboWbData);
          if (fboFromTable != null) updatePayload.commission_fbo = fboFromTable;
        }
        if (calculatedCosts) {
          if (calculatedCosts.target_price_rub_wb != null) updatePayload.target_price_rub_wb = calculatedCosts.target_price_rub_wb;
          if (calculatedCosts.target_price_cny_wb != null) updatePayload.target_price_cny_wb = calculatedCosts.target_price_cny_wb;
          if (calculatedCosts.target_price_cny_ozon != null) updatePayload.target_price_cny_ozon = calculatedCosts.target_price_cny_ozon;
          if (calculatedCosts.target_price_rub_ozon != null) updatePayload.target_price_rub_ozon = calculatedCosts.target_price_rub_ozon;
        }
      }

      if (Object.keys(updatePayload).length === 0) return;

      await api.updateProduct(productId, updatePayload);
      if (updates.article !== undefined) {
        setArticleFieldErrors((prev) => {
          if (!prev[productId]) return prev;
          const next = { ...prev };
          delete next[productId];
          return next;
        });
      }
      
      // Обновляем Redux state после успешного сохранения
      dispatch(updateProduct({ 
        productId, 
        updates: {
          requiredPrice: updates.requiredPrice,
          certificate: updates.certificate,
          orderQuantity: updates.orderQuantity,
          packageLength: updates.packageLength,
          packageWidth: updates.packageWidth,
          packageHeight: updates.packageHeight,
          ...(updatePayload.commission_fbo != null && { commission_fbo: updatePayload.commission_fbo }),
          purchasePercent: updates.purchasePercent,
          name: updates.name,
          target_price_cny: updates.manualYuanPrice,
          ...(updatePayload.article != null && { article: updatePayload.article })
        }
      }));
    } catch (error: any) {
      console.error('Error saving product to DB:', error);
      if (updates.article !== undefined) {
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          'Не удалось сохранить артикул';
        setArticleFieldErrors((prev) => ({ ...prev, [productId]: msg }));
      }
    } finally {
      setUpdatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [products, dispatch, calculateProductCosts, allSubjects, fboWbData]);

  // Обновление данных товара: сначала сырые поля (без тяжёлого расчёта), пересчёт — в startTransition
  const updateProductData = useCallback((productId: string, updates: Partial<ProductData>, saveImmediately = false) => {
    const product = products.find(p => p.id === productId);
    const isSaved = product?.isSaved === true;

    const calculationFields: (keyof ProductData)[] = [
      'requiredPrice', 'packageLength', 'packageWidth', 'packageHeight', 
      'purchasePercent', 'certificate', 'orderQuantity'
    ];

    const currentData = productDataRef.current[productId] || {
      id: productId,
      name: '',
      requiredPrice: 0,
      certificate: '',
      orderQuantity: '',
      packageLength: 0,
      packageWidth: 0,
      packageHeight: 0,
      purchasePercent: 0,
      manualYuanPrice: null
    };
    const newData = { ...currentData, ...updates };
    const hasCalculationFieldChanged = calculationFields.some(field => 
      updates[field] !== undefined && updates[field] !== currentData[field]
    );

    const currentOriginalPrice = originalYuanPricesRef.current[productId];
    if (hasCalculationFieldChanged && currentOriginalPrice === undefined) {
      const currentYuanPrice = currentData.manualYuanPrice !== null && currentData.manualYuanPrice !== undefined
        ? currentData.manualYuanPrice
        : (currentData.calculatedYuanPrice !== undefined ? currentData.calculatedYuanPrice : null);
      setOriginalYuanPrices(prevPrices => ({
        ...prevPrices,
        [productId]: currentYuanPrice
      }));
      originalYuanPricesRef.current[productId] = currentYuanPrice;
    }

    setProductData(prev => ({
      ...prev,
      [productId]: {
        ...newData,
        calculatedCostPrice: currentData.calculatedCostPrice,
        calculatedYuanPrice: currentData.calculatedYuanPrice
      }
    }));

    startTransition(() => {
      const calculatedCosts = calculateProductCosts(newData, product);
      const newCalculatedYuanPrice = calculatedCosts?.yuanPrice;

      if (hasCalculationFieldChanged && newCalculatedYuanPrice !== undefined) {
        const originalPrice = originalYuanPricesRef.current[productId];
        if (originalPrice !== undefined) {
          const originalRounded = originalPrice !== null ? Math.round(originalPrice * 100) / 100 : null;
          const newRounded = Math.round(newCalculatedYuanPrice * 100) / 100;
          if (originalRounded !== newRounded) {
            setRecalculatedYuanPrices(prev => ({ ...prev, [productId]: true }));
          }
        }
      }

      setProductData(prev => {
        const existing = prev[productId];
        if (!existing) return prev;
        const costPrice = calculatedCosts?.costPrice ?? existing.calculatedCostPrice;
        const yuanPrice = newCalculatedYuanPrice ?? existing.calculatedYuanPrice;
        if (existing.calculatedCostPrice === costPrice && existing.calculatedYuanPrice === yuanPrice) return prev;
        return {
          ...prev,
          [productId]: { ...existing, calculatedCostPrice: costPrice, calculatedYuanPrice: yuanPrice }
        };
      });
    });

    // Автосохранение для уже сохраненных товаров
    if (isSaved && !saveImmediately) {
      // Очищаем предыдущий таймер для этого товара
      if (saveTimersRef.current[productId]) {
        clearTimeout(saveTimersRef.current[productId]);
      }
      
      // Debounce автосохранения (800ms), чтобы не спамить БД при быстром вводе/переключении полей
      const SAVE_DEBOUNCE_MS = 800;
      saveTimersRef.current[productId] = setTimeout(() => {
        saveProductToDB(productId, updates);
        delete saveTimersRef.current[productId];
      }, SAVE_DEBOUNCE_MS);
    } else if (isSaved && saveImmediately) {
      // Немедленное сохранение (например, при onBlur)
      if (saveTimersRef.current[productId]) {
        clearTimeout(saveTimersRef.current[productId]);
        delete saveTimersRef.current[productId];
      }
      saveProductToDB(productId, updates);
    }
  }, [calculateProductCosts, products, saveProductToDB]);

  // Обработчик изменения названия товара
  const handleNameChange = useCallback((productId: string, name: string) => {
    dispatch(updateProduct({ productId, updates: { name } }));
    updateProductData(productId, { name });
  }, [dispatch, updateProductData]);

  // Обработчик изменения subject: сохраняем subject_id и FBO % из таблицы по предмету
  const handleSubjectChange = useCallback(async (productId: string, subjectId: number | null) => {
    dispatch(updateProduct({ productId, updates: { subject_id: subjectId } }));
    dispatch(clearValidationErrors(productId));
    if (subjectId != null && fboWbData.length > 0) {
      const subjectName = allSubjects.find(s => s.id === subjectId)?.name ?? '';
      const fboFromTable = getFboWb(subjectName, fboWbData);
      if (fboFromTable != null) {
        try {
          await api.updateProduct(productId, { commission_fbo: fboFromTable });
          dispatch(updateProduct({ productId, updates: { commission_fbo: fboFromTable } }));
        } catch (e) {
          console.error('Failed to save FBO from table:', e);
        }
      }
    }
  }, [dispatch, allSubjects, fboWbData]);

  // Обработчик подтверждения пересчитанной цены
  const handleConfirmRecalculatedPrice = useCallback(async (productId: string) => {
    const data = productData[productId];
    if (!data) return;
    const product = products.find(p => p.id === productId);

    const newPrice = data.manualYuanPrice !== null && data.manualYuanPrice !== undefined
      ? data.manualYuanPrice
      : (data.calculatedYuanPrice !== undefined ? data.calculatedYuanPrice : null);

    if (newPrice !== null && newPrice !== undefined && !isNaN(newPrice)) {
      setUpdatingProducts(prev => new Set(prev).add(productId));
      try {
        const payload: Record<string, number | null> = { target_price_cny: newPrice };
        const calculatedCosts = calculateProductCosts(data, product);
        if (calculatedCosts) {
          if (calculatedCosts.target_price_rub_wb != null) payload.target_price_rub_wb = calculatedCosts.target_price_rub_wb;
          if (calculatedCosts.target_price_cny_wb != null) payload.target_price_cny_wb = calculatedCosts.target_price_cny_wb;
          if (calculatedCosts.target_price_cny_ozon != null) payload.target_price_cny_ozon = calculatedCosts.target_price_cny_ozon;
          if (calculatedCosts.target_price_rub_ozon != null) payload.target_price_rub_ozon = calculatedCosts.target_price_rub_ozon;
        }
        await api.updateProduct(productId, payload);
        // Обновить оригинальное значение
        setOriginalYuanPrices(prev => ({ ...prev, [productId]: newPrice }));
        originalYuanPricesRef.current[productId] = newPrice;
        // Снять пометку пересчета
        setRecalculatedYuanPrices(prev => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
        // Обновить Redux state
        dispatch(updateProduct({ 
          productId, 
          updates: { target_price_cny: newPrice } 
        }));
      } catch (error) {
        console.error('Failed to confirm recalculated price:', error);
        alert('Ошибка при сохранении цены');
      } finally {
        setUpdatingProducts(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
      }
    }
  }, [productData, products, dispatch, calculateProductCosts]);

  // Обработчик отмены пересчитанной цены
  const handleRevertRecalculatedPrice = useCallback((productId: string) => {
    const originalPrice = originalYuanPrices[productId];
    if (originalPrice !== undefined) {
      // Восстановить оригинальное значение
      updateProductData(productId, { manualYuanPrice: originalPrice });
      // Снять пометку пересчета
      setRecalculatedYuanPrices(prev => {
        const newState = { ...prev };
        delete newState[productId];
        return newState;
      });
    }
  }, [originalYuanPrices, updateProductData]);

  // Функция проверки готовности строки для подтверждения
  const isRowComplete = useCallback((productId: string): boolean => {
    const product = products.find(p => p.id === productId);
    const data = productData[productId];
    if (!data || !product) return false;
    
    // Проверяем все обязательные поля для подтверждения
    return !!(
      data.orderQuantity &&
      data.packageLength > 0 &&
      data.packageWidth > 0 &&
      data.packageHeight > 0 &&
      data.calculatedCostPrice !== undefined &&
      data.calculatedYuanPrice !== undefined
    );
  }, [products, productData]);

  // Обработчик изменения URL
  const handleUrlChange = useCallback((productId: string, url: string) => {
    dispatch(updateProduct({ productId, updates: { url } }));
    dispatch(clearValidationErrors(productId));
  }, [dispatch]);

  // Обработчик загрузки изображения
  const handleImageUpload = useCallback((productId: string, file: File) => {
    imageFilesRef.current[productId] = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      dispatch(updateProduct({ 
        productId, 
        updates: { thumb_url: imageUrl, thumb_middle_url: imageUrl } 
      }));
      dispatch(clearValidationErrors(productId));
    };
    reader.readAsDataURL(file);
  }, [dispatch]);

  // Обработчик удаления изображения
  const handleImageDelete = useCallback((productId: string) => {
    dispatch(updateProduct({ 
      productId, 
      updates: { thumb_url: null, thumb_middle_url: null } 
    }));
    delete imageFilesRef.current[productId];
    const fileInput = fileInputRefs.current[productId];
    if (fileInput) {
      fileInput.value = '';
    }
  }, [dispatch]);

  // Глобальный обработчик paste для вставки изображений из буфера обмена
  useEffect(() => {
    const handleGlobalPaste = async (event: ClipboardEvent) => {
      if (!selectedProductId) return;
      
      const items = event.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.indexOf('image') !== -1) {
            const file = item.getAsFile();
            if (file) {
              // В NewWave.tsx изображение сохраняется только в thumb_url (одно изображение)
              handleImageUpload(selectedProductId, file);
            }
            break;
          }
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, [selectedProductId, handleImageUpload]);

  // Обработчики drag and drop
  const handleDragStart = useCallback((e: React.DragEvent, productId: string, fieldType: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity' | 'subject_id' | 'purchasePercent', value: number | string) => {
    setDraggedProductId(productId);
    setDraggedFieldType(fieldType);
    setDraggedValue(value);
    setIsDragging(true);
    setDragOverProductIds(new Set([productId]));
    if (['packageLength', 'packageWidth', 'packageHeight'].includes(fieldType)) {
      setDragOverFieldTypes(new Set([fieldType as 'packageLength' | 'packageWidth' | 'packageHeight']));
    } else {
      setDragOverFieldTypes(new Set());
    }
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, targetProductId: string, targetFieldType?: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity' | 'subject_id' | 'purchasePercent') => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedProductId || !draggedFieldType) return;

    const sourceIndex = products.findIndex(p => p.id === draggedProductId);
    const targetIndex = products.findIndex(p => p.id === targetProductId);
    if (sourceIndex === -1 || targetIndex === -1) return;

    const startIndex = Math.min(sourceIndex, targetIndex);
    const endIndex = Math.max(sourceIndex, targetIndex);
    const affectedProductIds = products.slice(startIndex, endIndex + 1).map(p => p.id);
    let affectedFieldTypes: Set<'packageLength' | 'packageWidth' | 'packageHeight'>;

    if (targetFieldType &&
        ['packageLength', 'packageWidth', 'packageHeight'].includes(draggedFieldType) &&
        ['packageLength', 'packageWidth', 'packageHeight'].includes(targetFieldType)) {
      const sizeFields: ('packageLength' | 'packageWidth' | 'packageHeight')[] = ['packageLength', 'packageWidth', 'packageHeight'];
      const sourceFieldIndex = sizeFields.indexOf(draggedFieldType as 'packageLength' | 'packageWidth' | 'packageHeight');
      const targetFieldIndex = sizeFields.indexOf(targetFieldType as 'packageLength' | 'packageWidth' | 'packageHeight');
      if (sourceFieldIndex !== -1 && targetFieldIndex !== -1) {
        const startFieldIndex = Math.min(sourceFieldIndex, targetFieldIndex);
        const endFieldIndex = Math.max(sourceFieldIndex, targetFieldIndex);
        affectedFieldTypes = new Set(sizeFields.slice(startFieldIndex, endFieldIndex + 1));
      } else {
        affectedFieldTypes = new Set([draggedFieldType as 'packageLength' | 'packageWidth' | 'packageHeight']);
      }
    } else {
      if (['packageLength', 'packageWidth', 'packageHeight'].includes(draggedFieldType)) {
        affectedFieldTypes = new Set([draggedFieldType as 'packageLength' | 'packageWidth' | 'packageHeight']);
      } else {
        affectedFieldTypes = new Set();
      }
    }

    dragOverStateRef.current = { productIds: new Set(affectedProductIds), fieldTypes: affectedFieldTypes };
    if (dragOverRafRef.current === null) {
      dragOverRafRef.current = requestAnimationFrame(() => {
        const state = dragOverStateRef.current;
        if (state) {
          setDragOverProductIds(state.productIds);
          setDragOverFieldTypes(state.fieldTypes);
        }
        dragOverRafRef.current = null;
        dragOverStateRef.current = null;
      });
    }
  }, [draggedProductId, draggedFieldType, products]);

  const handleDragEnd = useCallback(() => {
    if (dragOverRafRef.current !== null) {
      cancelAnimationFrame(dragOverRafRef.current);
      dragOverRafRef.current = null;
    }
    dragOverStateRef.current = null;
    setDraggedProductId(null);
    setDraggedFieldType(null);
    setDraggedValue(null);
    setDragOverProductIds(new Set());
    setDragOverFieldTypes(new Set());
    setIsDragging(false);
  }, []);

  const handleBatchFieldChange = useCallback((productIds: string[], fieldType: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity' | 'subject_id' | 'purchasePercent', value: number | string) => {
    if (fieldType === 'subject_id') {
      // Для subject_id используем handleSubjectChange
      productIds.forEach(productId => {
        handleSubjectChange(productId, value as number | null);
      });
      return;
    }
    
    const updates: Partial<ProductData> = {};
    switch (fieldType) {
      case 'packageLength': updates.packageLength = value as number; break;
      case 'packageWidth': updates.packageWidth = value as number; break;
      case 'packageHeight': updates.packageHeight = value as number; break;
      case 'requiredPrice': updates.requiredPrice = value as number; break;
      case 'certificate': updates.certificate = value as string; break;
      case 'orderQuantity': break; // обрабатываем отдельно с учётом цены каждого товара
      case 'purchasePercent': updates.purchasePercent = value as number; break;
    }
    if (fieldType === 'orderQuantity') {
      productIds.forEach(productId => {
        const data = productData[productId];
        const product = products.find(p => p.id === productId);
        const price = data?.requiredPrice ?? product?.requiredPrice ?? product?.final_price_average ?? product?.final_price ?? 0;
        updateProductData(productId, { orderQuantity: roundOrderQuantity(value as string | number, price) }, true);
      });
    } else {
      productIds.forEach(productId => {
        updateProductData(productId, updates, true);
      });
    }
  }, [updateProductData, roundOrderQuantity, handleSubjectChange, productData, products]);

  const handleDrop = useCallback(async (e: React.DragEvent, targetProductId: string, targetFieldType?: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity' | 'subject_id' | 'purchasePercent') => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedProductId || !draggedFieldType || draggedValue === null) return;

    const affectedProductIds = Array.from(dragOverProductIds);
    const affectedFieldTypes = Array.from(dragOverFieldTypes);

    if (affectedProductIds.length > 0) {
      if (targetFieldType && 
          ['packageLength', 'packageWidth', 'packageHeight'].includes(draggedFieldType) &&
          ['packageLength', 'packageWidth', 'packageHeight'].includes(targetFieldType)) {
        await Promise.all(affectedFieldTypes.map(fieldType => 
          handleBatchFieldChange(affectedProductIds, fieldType, draggedValue)
        ));
      } else {
        await handleBatchFieldChange(affectedProductIds, draggedFieldType, draggedValue);
      }
    }

    handleDragEnd();
  }, [draggedProductId, draggedFieldType, draggedValue, dragOverProductIds, dragOverFieldTypes, handleBatchFieldChange, handleDragEnd]);

  // Валидация перед сохранением
  const validateProduct = useCallback((product: NewWaveProduct, data: ProductData): NewWaveProduct['validationErrors'] => {
    const errors: NewWaveProduct['validationErrors'] = {};
    if (!product.id || product.id === '0') errors.id = true;
    if (!product.subject_id || product.subject_id === 0) errors.subject_id = true;
    if (!product.name || product.name.trim() === '') errors.name = true;
    if (!product.url || product.url.trim() === '') errors.url = true;
    if (!product.thumb_url || product.thumb_url.trim() === '') errors.thumb_url = true;
    if (!data.orderQuantity || data.orderQuantity.trim() === '') errors.orderQuantity = true;
    if (!data.packageLength || data.packageLength <= 0) errors.packageLength = true;
    if (!data.packageWidth || data.packageWidth <= 0) errors.packageWidth = true;
    if (!data.packageHeight || data.packageHeight <= 0) errors.packageHeight = true;
    if (!data.requiredPrice || data.requiredPrice <= 0) errors.requiredPrice = true;
    // Проверка обязательности цены в юанях
    const yuanPrice = data.manualYuanPrice !== null && data.manualYuanPrice !== undefined 
      ? data.manualYuanPrice 
      : (data.calculatedYuanPrice !== undefined && typeof data.calculatedYuanPrice === 'number' && !isNaN(data.calculatedYuanPrice) ? data.calculatedYuanPrice : null);
    if (yuanPrice === null || yuanPrice === undefined || (typeof yuanPrice === 'number' && (isNaN(yuanPrice) || yuanPrice <= 0))) {
      errors.manualYuanPrice = true;
    }
    return Object.keys(errors).length > 0 ? errors : undefined;
  }, []);

  // Обработчик сохранения товара
  const handleSaveProduct = useCallback(async (product: NewWaveProduct) => {
    const data = productData[product.id] || {
      id: product.id,
      name: product.name || '',
      requiredPrice: product.final_price_average || product.final_price || 0,
      certificate: '',
      orderQuantity: '',
      packageLength: 0,
      packageWidth: 0,
      packageHeight: 0,
      commissionFbo: product.commission_fbo || 0,
      purchasePercent: 0,
      manualYuanPrice: null
    };

    const validationErrors = validateProduct(product, data);
    if (validationErrors) {
      dispatch(setValidationErrors({ productId: product.id, errors: validationErrors }));
      setLocalValidationErrors(prev => ({ ...prev, [product.id]: validationErrors }));
      return;
    }

    setSavingProducts(prev => new Set(prev).add(product.id));
    
    try {
      let imageFile: File | undefined = imageFilesRef.current[product.id];
      if (!imageFile) {
        const fileInput = fileInputRefs.current[product.id];
        if (fileInput && fileInput.files && fileInput.files.length > 0) {
          imageFile = fileInput.files[0];
        }
      }

      const productDataToSave = {
        id: product.id,
        name: product.name || 'Нет названия',
        subject_id: product.subject_id,
        url: product.url,
        thumb_url: product.thumb_url,
        thumb_middle_url: product.thumb_middle_url || product.thumb_url,
        requiredPrice: data.requiredPrice,
        certificate: data.certificate,
        orderQuantity: data.orderQuantity,
        packageLength: data.packageLength,
        packageWidth: data.packageWidth,
        packageHeight: data.packageHeight,
        purchasePercent: data.purchasePercent,
        commission_fbo: product.subject_id != null
          ? (getFboWb(allSubjects.find(s => s.id === product.subject_id)?.name ?? '', fboWbData) ?? product.commission_fbo ?? undefined)
          : (product.commission_fbo ?? undefined),
        // Все остальные данные из MPStats API
        balance: product.balance,
        comments: product.comments,
        rating: product.rating,
        final_price: product.final_price,
        final_price_max: product.final_price_max,
        final_price_min: product.final_price_min,
        final_price_average: product.final_price_average,
        final_price_median: product.final_price_median,
        sales: product.sales,
        sales_per_day_average: product.sales_per_day_average,
        days_in_stock: product.days_in_stock,
        days_with_sales: product.days_with_sales,
        average_if_in_stock: product.average_if_in_stock,
        commission_fbs: product.commission_fbs,
        search_words_count: product.search_words_count,
        search_position_avg: product.search_position_avg,
        search_visibility: product.search_visibility,
        warehouses_count: product.warehouses_count,
        size_count: product.size_count,
        size_count_in_stock: product.size_count_in_stock,
        graph: product.graph,
        stocks_graph: product.stocks_graph,
        price_graph: product.price_graph,
        product_visibility_graph: product.product_visibility_graph,
        category_graph: product.category_graph,
        category_position_graph: product.category_position_graph,
        search_position_graph: product.search_position_graph,
        warehouses_count_graph: product.warehouses_count_graph,
        size_count_in_stock_graph: product.size_count_in_stock_graph
      };

      await dispatch(saveNewWaveProduct({ 
        productId: product.id, 
        productData: productDataToSave, 
        imageFile 
      })).unwrap();
      
      // После успешного сохранения проверяем условия и подтверждаем товар автоматически
      if (isRowComplete(product.id)) {
        if (isPlaceholderPurchasePercent(data.purchasePercent)) {
          dispatch(setValidationErrors({ productId: product.id, errors: { purchasePercent: true } }));
          alert('Подтверждение запрещено: укажите процент выкупа, отличный от 100%');
        } else {
          try {
          const calculatedCosts = calculateProductCosts(data, product);
          // Используем ручное значение если задано, иначе рассчитанное
          const targetPriceCny = data.manualYuanPrice !== null && data.manualYuanPrice !== undefined
            ? data.manualYuanPrice
            : (calculatedCosts && typeof calculatedCosts.yuanPrice === 'number' && !isNaN(calculatedCosts.yuanPrice) 
                ? calculatedCosts.yuanPrice 
                : null);
          
          await dispatch(confirmNewProduct({ 
            productId: product.id, 
            confirmed: true,
            targetPriceCny
          })).unwrap();
        } catch (error: any) {
          console.error('Failed to auto-confirm product:', error);
          if (error?.validationErrors?.purchasePercent) {
            dispatch(setValidationErrors({ productId: product.id, errors: { purchasePercent: true } }));
            alert('Подтверждение запрещено: укажите процент выкупа, отличный от 100%');
          }
        }
        }
      }
    } catch (error: any) {
      console.error('Failed to save product:', error);
      // Показываем ошибку пользователю
      // Ошибка может быть в разных форматах:
      // - Из Redux thunk rejectWithValue: error.payload?.error или error.error
      // - Из Axios: error.response?.data?.error
      // - Прямое сообщение: error.message
      const errorMessage = error?.payload?.error || 
                          error?.error || 
                          error?.message || 
                          error?.response?.data?.error || 
                          'Ошибка при сохранении товара';
      alert(errorMessage);
    } finally {
      setSavingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }
  }, [dispatch, validateProduct, productData, isRowComplete, calculateProductCosts]);

  // Обработчик удаления товара из списка
  const handleRemoveProduct = useCallback(async (productId: string) => {
    if (window.confirm('Удалить товар из списка?')) {
      try {
        await api.deleteNewWaveProduct(productId);
        dispatch(removeProduct(productId));
        setProductData(prev => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });
        delete imageFilesRef.current[productId];
      } catch (error: any) {
        console.error('Failed to delete product:', error);
        alert('Ошибка при удалении товара: ' + (error.response?.data?.error || error.message || 'Неизвестная ошибка'));
      }
    }
  }, [dispatch]);

  // Форматирование (стабильные ссылки для мемоизированной строки)
  const formatSubjectName = useCallback((subjectName: string) => {
    if (!subjectName) return '';
    const slashIndex = subjectName.indexOf('/');
    return slashIndex !== -1 ? subjectName.substring(slashIndex + 1) : subjectName;
  }, []);

  const formatCurrency = useCallback((amount: number) => {
    if (!amount) return '0 ₽';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }, []);

  const getFullImageUrl = useCallback((url: string | null) => {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    if (url.startsWith('//')) return `https:${url}`;
    if (url.startsWith('/')) return url;
    return url;
  }, []);

  const subjectOptions = useMemo(() => allSubjects.map(subject => ({
    value: subject.id,
    label: formatSubjectName(subject.name)
  })), [allSubjects]);

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">New Wave</h2>

      {/* Форма поиска по SKU */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <IconSearch className="me-2" />
            Добавить товар по SKU
          </h5>
        </Card.Header>
        <Card.Body>
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Введите SKU товара"
              value={skuInput}
              onChange={(e) => setSkuInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSku();
                }
              }}
              disabled={fetchingSku}
              style={{ maxWidth: '300px' }}
            />
            <Button 
              variant="primary" 
              onClick={handleSearchSku}
              disabled={fetchingSku || !skuInput.trim()}
            >
              {fetchingSku ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Поиск...
                </>
              ) : (
                'Найти товар'
              )}
            </Button>
          </div>
          {fetchingSkuError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {fetchingSkuError}
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="mt-3 mb-0" onClose={() => dispatch(clearError())} dismissible>
              {error}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Таблица товаров */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              Добавленные товары
              {(pagination.total > 0 || products.length > 0) && (
                <Badge bg="primary" className="ms-2">
                  {pagination.total > 0 ? pagination.total : products.length} товаров
                </Badge>
              )}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm" id="checker-dropdown-newwave">
                  <IconCheckSquare size={14} className="me-1" />
                  Чекер
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => { saveCheckerPanelState({ open: true }); setCheckerModal({ show: true }); }}>
                    Открыть тут
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => window.open('/products/checker', '_blank')}>
                    Открыть в новой вкладке
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => window.open('/products/checker', '_blank', 'noopener,noreferrer,width=900,height=700')}>
                    Открыть в новом окне
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {products.length > 0 && (
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => {
                    if (window.confirm('Очистить весь список?')) {
                      products.forEach(p => dispatch(removeProduct(p.id)));
                    }
                  }}
                >
                  <IconTrash className="me-1" />
                  Очистить все
                </Button>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {products.length === 0 ? (
            <Alert variant="info">
              Список товаров пуст. Введите SKU для добавления товара.
            </Alert>
          ) : (
            <>
            <div style={{ overflowX: 'auto' }}>
            <Table responsive variant="dark" className="table-dark">
              <thead>
                <tr>
                  <th>img</th>
                  <th>SKU</th>
                  <th>Артикул</th>
                  <th>Название</th>
                  <th>Предмет WB</th>
                  <th>URL товара</th>
                  <th>Изображение</th>
                  <th>Нужная цена на wb</th>
                  <th>Сертификат</th>
                  <th>Заказ, количество</th>
                  <th>Длина упак. тары, см</th>
                  <th>Ширина упак. тары, см</th>
                  <th>Высота упак. тары, см</th>
                  <th>%Выкуп</th>
                  <th>Себестоимость, руб.</th>
                  <th>Итог цены в юанях</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const data = productData[product.id] || {
                    id: product.id,
                    name: product.name || '',
                    requiredPrice: product.final_price_average || product.final_price || 0,
                    certificate: '',
                    orderQuantity: '',
                    packageLength: 0,
                    packageWidth: 0,
                    packageHeight: 0,
                    purchasePercent: product.purchasePercent || 0,
                    manualYuanPrice: product.target_price_cny !== null && product.target_price_cny !== undefined ? product.target_price_cny : null
                  };
                  const hasErrors = product.validationErrors && Object.keys(product.validationErrors).length > 0;
                  const errors = { ...(product.validationErrors || {}), ...(localValidationErrors[product.id] || {}) };
                  const yuanPrice = data.manualYuanPrice !== null && data.manualYuanPrice !== undefined ? data.manualYuanPrice : (data.calculatedYuanPrice !== undefined ? data.calculatedYuanPrice : null);
                  const calculatedCosts = data.calculatedCostPrice !== undefined && typeof data.calculatedCostPrice === 'number' && !isNaN(data.calculatedCostPrice)
                    ? { costPrice: data.calculatedCostPrice, yuanPrice: yuanPrice !== null && yuanPrice !== undefined && typeof yuanPrice === 'number' && !isNaN(yuanPrice) ? yuanPrice : undefined }
                    : null;

                  return (
                    <NewWaveRow
                      key={product.id}
                      product={product}
                      data={data}
                      errors={errors}
                      hasErrors={!!hasErrors}
                      calculatedCosts={calculatedCosts}
                      subjectOptions={subjectOptions}
                      certificates={certificates}
                      allSubjects={allSubjects}
                      tempInputValuesRow={tempInputValues[product.id]}
                      tempYuanPriceRow={tempYuanPrice[product.id]}
                      recalculatedYuan={!!recalculatedYuanPrices[product.id]}
                      isUpdating={updatingProducts.has(product.id)}
                      isSaving={savingProducts.has(product.id)}
                      isSelected={selectedProductId === product.id}
                      checkerModalShow={checkerModal.show}
                      isDragging={isDragging}
                      isDragOver={dragOverProductIds.has(product.id)}
                      draggedProductId={draggedProductId}
                      draggedFieldType={draggedFieldType}
                      dragOverFieldTypes={dragOverFieldTypes}
                      fileInputRefs={fileInputRefs}
                      onRowClick={setSelectedProductId}
                      setCheckerModal={setCheckerModal}
                      saveCheckerPanelState={saveCheckerPanelState}
                      onNameChange={handleNameChange}
                      onSubjectChange={handleSubjectChange}
                      onConfirmRecalculatedPrice={handleConfirmRecalculatedPrice}
                      onRevertRecalculatedPrice={handleRevertRecalculatedPrice}
                      onUrlChange={handleUrlChange}
                      onImageUpload={handleImageUpload}
                      onImageDelete={handleImageDelete}
                      onSaveProduct={handleSaveProduct}
                      onRemoveProduct={handleRemoveProduct}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      onUpdateProductData={updateProductData}
                      roundOrderQuantity={roundOrderQuantity}
                      formatCurrency={formatCurrency}
                      getFullImageUrl={getFullImageUrl}
                      formatSubjectName={formatSubjectName}
                      articleError={articleFieldErrors[product.id]}
                      onClearArticleError={() => setArticleFieldErrors((prev) => {
                        if (!prev[product.id]) return prev;
                        const next = { ...prev };
                        delete next[product.id];
                        return next;
                      })}
                      onTempYuanChange={(productId, value) => setTempYuanPrice(prev => ({ ...prev, [productId]: value }))}
                      onBlurYuan={(productId, value) => {
                        startTransition(() => setTempYuanPrice(prev => { const n = { ...prev }; delete n[productId]; return n; }));
                        if (value) { const num = parseFloat(value); if (!isNaN(num)) updateProductData(productId, { manualYuanPrice: num }); } else { updateProductData(productId, { manualYuanPrice: null }); }
                      }}
                    />
                  );
                })}
              </tbody>
            </Table>
            </div>
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <Pagination className="mb-0">
                  <Pagination.First
                    onClick={() => dispatch(setNewWavePagination({ page: 1 }))}
                    disabled={pagination.page === 1}
                  />
                  <Pagination.Prev
                    onClick={() => dispatch(setNewWavePagination({ page: Math.max(1, pagination.page - 1) }))}
                    disabled={pagination.page === 1}
                  />
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() => dispatch(setNewWavePagination({ page: pageNum }))}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    onClick={() => dispatch(setNewWavePagination({ page: Math.min(pagination.totalPages, pagination.page + 1) }))}
                    disabled={pagination.page === pagination.totalPages}
                  />
                  <Pagination.Last
                    onClick={() => dispatch(setNewWavePagination({ page: pagination.totalPages }))}
                    disabled={pagination.page === pagination.totalPages}
                  />
                </Pagination>
                <span className="text-muted small">
                  Страница {pagination.page} из {pagination.totalPages}
                </span>
              </div>
            )}
            </>
          )}
        </Card.Body>
      </Card>

      <CheckerModal
        show={checkerModal.show}
        onHide={() => { saveCheckerPanelState({ open: false }); setCheckerModal({ show: false }); }}
        initialSku={checkerModal.initialSku}
        initialSearchAsSubject={checkerModal.initialSearchAsSubject ?? false}
      />
    </Container>
  );
};

export default NewWave;
